import { useEffect, useState } from 'react';
import {
  LOCATION_TROUBLESHOOTING,
  recoveryAction,
  recoveryLabel,
  recoveryReason,
  type LocationPermission,
} from '../../shared/locationRecovery';

/**
 * v22 BOLT-110 — the location warning, with an ACTION and a way out.
 *
 * It used to be a dead end: "Bolt needs your location", and nothing to press.
 * Now it does the right thing per state, and carries a "why?" affordance with
 * plain steps. Paired with BOLT-115's live reason and accuracy, the player is
 * told what is wrong AND how to fix it.
 *
 * Web reality: a browser will not re-show its native prompt after a denial and
 * cannot open its own site settings, so the denied case is an honest
 * instruction rather than a fake button. iOS deep-links properly (see
 * LocationRecovery.swift).
 */
export function LocationHelp({ reason }: { reason?: string | null }) {
  const [permission, setPermission] = useState<LocationPermission>('notDetermined');
  const [showHelp, setShowHelp] = useState(false);
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    let live = true;
    const read = async () => {
      try {
        const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
        if (!live || !status) return;
        const map = () =>
          status.state === 'granted' ? 'precise' : status.state === 'denied' ? 'denied' : 'notDetermined';
        setPermission(map() as LocationPermission);
        status.onchange = () => live && setPermission(map() as LocationPermission);
      } catch {
        /* Permissions API unavailable: leave it at notDetermined, where the
           prompt is still the correct thing to offer. */
      }
    };
    void read();
    return () => {
      live = false;
    };
  }, []);

  const action = recoveryAction(permission);
  // A browser cannot re-prompt after a denial, so say where to go instead of
  // pretending a button can do it.
  const canAct = action === 'request' && !asked;

  return (
    <div
      className="banner banner--signal"
      data-testid="needs-gps"
      style={{ marginTop: '0.75rem', flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem' }}
    >
      <span className="banner__detail" data-testid="gps-reason">
        {reason || recoveryReason(permission) || 'Bolt needs your location to play.'}
      </span>
      <div className="row" style={{ gap: '0.5rem' }}>
        {canAct && (
          <button
            className="btn btn--signal"
            style={{ width: 'auto' }}
            data-testid="gps-fix"
            onClick={() => {
              setAsked(true);
              // The native prompt: only reachable from notDetermined.
              navigator.geolocation.getCurrentPosition(
                () => setPermission('precise'),
                () => setPermission('denied'),
                { enableHighAccuracy: true },
              );
            }}
          >
            {recoveryLabel(action)}
          </button>
        )}
        <button
          className="btn btn--ghost"
          style={{ width: 'auto' }}
          data-testid="gps-help-toggle"
          aria-expanded={showHelp}
          onClick={() => setShowHelp((v) => !v)}
        >
          {showHelp ? 'Hide help' : 'Why?'}
        </button>
      </div>
      {showHelp && (
        <ol className="eyebrow" data-testid="gps-help" style={{ textAlign: 'left', margin: 0, paddingLeft: '1.1rem' }}>
          {LOCATION_TROUBLESHOOTING.map((step) => (
            <li key={step} style={{ marginBottom: '0.25rem' }}>
              {step}
            </li>
          ))}
          {action === 'openSettings' && (
            <li>In your browser, open site settings for this page and set Location to Allow.</li>
          )}
        </ol>
      )}
    </div>
  );
}
